// Take a leg off a ticket, put one on, and price what is left either way.
//
// A punter reads a four-leg multi and likes three of them — or likes all four
// and knows a fifth. Until now the whole ticket was the offer: take it as
// printed, or take nothing. That is the app putting its own deal ahead of the
// bet somebody actually wants on, and the answer is not a new strategy — it is
// the same ticket with a selection struck out or added and every number
// underneath it re-done.
//
// EVERY NUMBER, which is the point of the file. Moving a leg moves the odds,
// the return, the combined chance, the edge, the EV and the colour the ticket
// prints in, and a screen that struck the leg out and left the old odds on the
// paper would be lying in the direction that flatters the bet. So this runs the
// same arithmetic the engine ran, off the SAME legs at the SAME prices — it is
// not a re-deal, nothing is re-shopped, and no board is asked for a new price.
//
// The two engines meet here, and they carry their numbers under different
// names. `runStrategy` writes `modelProbability`/`fairProbability` onto a leg
// and prices EV off the model (`engine/strategy.js`); Classic has no model at
// all — its legs carry `impliedProbability` (1/price) and a `pFair` read off the
// board — so its EV comes from the fair price (`strategies/classic.js`). Both
// rules are honoured below rather than averaged into one that is right for
// neither.
//
// Pure: no refs, no storage, no clock of its own, no fetch. A ticket goes in, a
// ticket comes out, `null` is the answer to everything it cannot do, and the
// two refusals a punter can actually cause are thrown in the words they read.
//
// The picker's own arithmetic is here too — `rankCandidates` and the two pure
// helpers the sheet reads through — because "which leg could go on this ticket,
// and is its price any good" is a question about the board and the ticket, not
// about a screen.

import { combineLegs } from './combine.js';
import { tierFor } from './strategy.js';
import { bestPriceToTake, bookFair, consensus, priceAt, selectionsOf } from './universe.js';
// Which market this is. A race and a fixture do not devig the same way — see
// `legForTicket` below, and `defaultDevig` in universe.js.
import { isRacingSport } from './teams.js';
// The market's fair price with the book being bet at left OUT of the vote —
// the excluded form, which is the one `outliers.js` and `runStrategy` both use
// when they ask whether a price is over the line. There is not a third edge in
// this app and there is not going to be one.
import { fairExcluding, MIN_BOOKS } from './outliers.js';
import { findStrategy } from './strategies/catalogue.js';
import { canonicalStrategyId } from './strategies/aliases.js';
// The leg shape, written down once and shared with the hand-logged single.
import { legFromMatch } from '../tickets.js';
// The same lookup `lib/margin.js` makes, for the same reason: which fixture a
// leg belongs to is a question this file must not answer for itself. A second
// opinion about that would let a ticket combine both sides of one match.
import { matchFor } from '../live-prices.js';
// How close to the start a ticket stops being placeable: zero, and the one
// definition of it lives with the timeline. A leg that has already started is
// not a leg the picker may offer.
import { LIVE_BUFFER_MS } from '../timeline.js';

const cents = (n) => Math.round(n * 100) / 100;

/**
 * How many legs a ticket's strategy prints at most.
 *
 * Read off the strategy document — Classic's own combiner says eight, which is
 * its outrageous tier — and canonicalised, so a ticket still carrying a retired
 * id is capped by the strategy it merged into. Five for a strategy this app
 * cannot look up: a saved fork, one since deleted. A cap of five is a number a
 * punter will recognise as a limit; no cap at all is a twelve-leg ticket nobody
 * meant to build.
 *
 * A TICKET WITH NO STRATEGY ON IT IS THE PUNTER'S OWN, and it has no cap: the
 * caps are what each strategy says about its own product, and nothing in this
 * app is entitled to tell somebody how many legs their own multi may have.
 */
export const DEFAULT_MAX_LEGS = 5;

function maxLegsFor(multi) {
  if (!multi?.strategyId) return Infinity;
  const found = findStrategy(canonicalStrategyId(multi.strategyId));
  const n = Number(found?.combiner?.maxLegs);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_MAX_LEGS;
}

// Spelled out, because the sentence is read rather than counted: "Five legs is
// the most" is how a person says it. Digits for anything past the table.
const WORDS = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
const word = (n) => WORDS[n] ?? String(n);

/** The two refusals a punter can actually cause, in the words they read. */
export const SAME_MATCH = 'That match is already on the ticket.';
export const legsFull = (n) => `${word(n)} legs is the most this strategy prints.`;
/** Not a refusal: the leg went on, at a price the ticket's own book is not paying. */
export const bestPriceUsed = (bookName) =>
  `${bookName} has no price on that match · best price used instead`;

/**
 * Which fixture a leg is on, under whichever name it carries one.
 *
 * `combineLegs` refuses two legs from the same match, and it tests `matchKey` —
 * which a Classic leg has never had. With every leg answering `undefined` the
 * check fires on the first pair and no Classic ticket could drop anything, so
 * the provider's event id stands in, and the board (where one is passed) is
 * asked first in case it knows the leg by both.
 *
 * A leg with no identity at all gets one of its own rather than the same blank
 * as every other: an unnamed leg is not the same match as another unnamed leg.
 */
function fixtureOf(leg, universe, i) {
  return leg?.matchKey ?? matchFor(universe, leg)?.matchKey ?? leg?.eventId ?? `leg-${i}`;
}

/** The probability a leg contributes to the ticket's own combined figure. */
function modelP(leg) {
  if (Number.isFinite(leg?.modelProbability)) return leg.modelProbability;
  if (Number.isFinite(leg?.impliedProbability)) return leg.impliedProbability;
  return 1 / Number(leg?.price);
}

/** What the leg is worth with its book's margin taken off, under either name. */
function fairP(leg) {
  if (Number.isFinite(leg?.fairProbability)) return leg.fairProbability;
  if (Number.isFinite(leg?.pFair)) return leg.pFair;
  return null;
}

/**
 * The ticket minus one leg: `{ multi, legs }`, or null where there is nothing
 * honest to print.
 *
 * @param {object} ticket    `{ multi, legs }` as the engine and the timeline hold it
 * @param {number} index     which leg to strike out, 0-based, as printed
 * @param {object} [universe] today's board — only ever read to name a leg's fixture
 *
 * The new ticket is a PROPOSAL like any other: `id` is the original's with
 * `-drop{index}` on the end, `derivedFrom` names the ticket it came from, and
 * the strategy, the label and the stake are the ones that were already on it.
 * The legs are re-stamped onto the new id, because a placed ticket's legs are
 * found by `multiId` and legs still pointing at their parent would be a bet
 * with nothing on it.
 *
 * Null when:
 *
 *   · the ticket is not a proposal. A placed bet is a record of money that has
 *     gone on; there is no version of this that reaches back into the bookie.
 *   · it is down to one leg. A single has nothing to drop, and a ticket with
 *     no legs is not a ticket.
 *   · the index is not one of its legs.
 *   · a leg carries no price to multiply. That is a broken ticket, not a
 *     shorter one.
 */
export function withoutLeg(ticket, index, universe = null) {
  const multi = ticket?.multi;
  const legs = Array.isArray(ticket?.legs) ? ticket.legs : [];
  if (!multi || multi.status !== 'proposed') return null;
  if (legs.length < 2) return null;
  const i = Number(index);
  if (!Number.isInteger(i) || i < 0 || i >= legs.length) return null;

  const kept = legs.filter((_, n) => n !== i);
  if (!kept.every(priced)) return null;
  return reprint(multi, kept, `${multi.id}-drop${i}`, universe);
}

/** Every price a ticket multiplies has to be an offer, not a placeholder. */
const priced = (leg) => Number.isFinite(Number(leg?.price)) && Number(leg.price) > 1;

/**
 * THE TICKET, RE-DONE OFF ITS LEGS — every number on it, under whichever
 * engine's rule the legs belong to. Dropping and adding both come through here,
 * because a ticket one leg shorter and a ticket one leg longer are the same
 * arithmetic and two copies of it would drift the day one was changed.
 */
function reprint(multi, legs, id, universe, { derived = true } = {}) {
  const combined = combineLegs(legs.map((leg, n) => ({
    matchKey: fixtureOf(leg, universe, n),
    price: Number(leg.price),
    p: modelP(leg),
  })));

  // Null the moment one leg cannot be priced fairly, rather than quietly
  // multiplying the rest: a fair figure that dropped a leg would overstate the
  // ticket. The same rule `classic.js` holds, said once for both engines.
  const fairs = legs.map(fairP);
  const fair = fairs.every((p) => Number.isFinite(p)) ? fairs.reduce((acc, p) => acc * p, 1) : null;

  const stake = Number(multi.stake);
  // A tuned Classic counted its long shots off the legs the BOARD supplied.
  // This ticket is one the punter made, so the count is no longer a fact about
  // it and the line it prints comes off with it.
  const { longShots: _shots, longShotsFound: _found, ...carried } = multi;

  const next = {
    ...carried,
    id,
    // WHICH INK IT PRINTS IN, off the combined chance — `tierFor`'s whole job
    // (`engine/strategy.js`). A shorter ticket is likelier and a longer one is
    // not, so the colour moves, and it should: a punter who drops the long shot
    // off an outrageous ticket is holding something else now.
    tier: tierFor(combined.p),
    combinedOdds: combined.price,
    combinedProbability: combined.p,
    potentialReturn: Number.isFinite(stake) ? cents(stake * combined.price) : null,
    fairProbability: fair,
    // Two engines, two rules, and the ticket's own legs say which it is. A
    // model ticket's EV is the model against the price it is taking; Classic
    // has no model, so its EV is the fair price against the same one.
    ev: Number.isFinite(multi.modelProbability)
      ? combined.p * combined.price - 1
      : (fair === null ? null : fair * combined.price - 1),
    // A ticket built from nothing came from nothing. `derivedFrom` is what
    // tells the timeline that an edit is its own line rather than a duplicate
    // of what it came from (`ticketKey`), and a new ticket has no parent to
    // be told apart from.
    ...(derived ? { derivedFrom: multi.id } : {}),
  };
  // Only where the original had them. Classic prints no model line, and a
  // `modelProbability` invented here would put one on it.
  if (Number.isFinite(multi.modelProbability)) {
    next.modelProbability = combined.p;
    next.edge = fair === null ? null : combined.p - fair;
  }

  return {
    multi: next,
    legs: legs.map((leg) => ({
      ...leg,
      id: `${id}-${leg.eventId ?? leg.matchKey ?? leg.id}`,
      multiId: id,
    })),
  };
}

/**
 * A leg for THIS ticket, off a match on the board: `{ leg, note }`, or null
 * where no book on the board is pricing that side.
 *
 * WHICH PRICE. A ticket that names its own book is a bet at that book, so the
 * leg is priced there and nowhere else; anything that does not name one — a
 * Classic multi, a best-price ticket — is priced at the punter's own shop,
 * which is where they are going to put it on. Only when that book has no price
 * on the match does the leg fall back to the longest price on the board, and
 * then it says so: `note` is a statement about what happened, not a refusal,
 * and the leg goes on either way.
 *
 * `fairProbability` is the book's OWN devigged vector, exactly as the engine
 * writes it on a leg it picked (`strategy.js`, `classic.js`) — multiplicative
 * on a fixture, the reading the ⓘ quotes, and SHIN ON A RACE.
 *
 * A race is the one market where the choice of method is not a rounding
 * difference: multiplicative charges the whole round proportionally, so the
 * shortest runner in a big field is handed a share of the margin it does not
 * carry and a $1.20 favourite reads as a 61% chance. A leg added to a ticket by
 * hand must print the same fair price as a leg the engine picked in the same
 * race, or the Fair/pays line on one ticket answers two ways. See `defaultDevig`
 * in universe.js.
 *
 * On a ticket that carries a model, the added leg is given the model's
 * probability as its fair one: the model was never asked about this match and
 * has no opinion, and a leg the model is neutral on is exactly the `model:
 * 'none'` case the engine already documents — its edge is zero. Inventing an
 * opinion here would move the ticket's edge on the strength of arithmetic
 * nobody ran.
 */
export function legForTicket(multi, match, side, { bookmaker = null, bookmakerName = null } = {}) {
  if (!multi || !match?.matchKey || !side) return null;
  const own = multi.bookmakerKey ?? bookmaker ?? null;
  const name = multi.bookmakerKey ? multi.bookmaker : (bookmakerName ?? null);

  const at = own ? priceAt(match, side, own) : null;
  const best = at === null ? bestPriceToTake(match, side) : null;
  const price = at ?? best?.price ?? null;
  const book = at === null ? (best?.bookmaker ?? null) : own;
  if (!Number.isFinite(price) || price <= 1) return null;

  const leg = legFromMatch(match, side, {
    price,
    bookmakerKey: book,
    fairProbability: bookFair(match, side, book, isRacingSport(match.sport) ? null : 'multiplicative'),
  });
  if (Number.isFinite(multi.modelProbability) && Number.isFinite(leg.fairProbability)) {
    leg.modelProbability = leg.fairProbability;
    leg.edge = 0;
  }
  return { leg, note: at === null && name ? bestPriceUsed(name) : '' };
}

/**
 * The ticket with one more leg on it: `{ multi, legs }`, or null where there is
 * nothing to build.
 *
 * @param {object} ticket    `{ multi, legs }`
 * @param {object} leg       a leg, as `legForTicket` builds one
 * @param {object} [universe] today's board — only ever read to name a fixture
 *
 * THROWS, with the sentence a punter reads, on the two refusals a punter can
 * actually cause: a second leg on a match already on the ticket, and a ticket
 * already as long as its strategy prints. They throw rather than answering null
 * for the reason `markPlaced` does — a refusal that came back as "no ticket"
 * would reach `useAction` as a success and toast a leg that never went on.
 * Null is kept for the structural cases, which no screen can offer: a ticket
 * that is not a proposal, and a leg with no price.
 *
 * The leg is slotted into KICK-OFF ORDER rather than stuck on the end, because
 * that is the order the engine prints a ticket in and this is the same ticket.
 */
export function withLeg(ticket, leg, universe = null) {
  return withLegs(ticket, [leg], universe);
}

/**
 * The same thing for a handful at once — which is what the picker builds: a
 * punter putting a combo together picks three matches in one visit, and three
 * separate reprices would put three tickets on the timeline and three rows on
 * the paper trail for one act.
 */
export function withLegs(ticket, added, universe = null) {
  const multi = ticket?.multi;
  const legs = Array.isArray(ticket?.legs) ? ticket.legs : [];
  const list = (Array.isArray(added) ? added : [added]).filter(Boolean);
  const mark = list.map((l, n) => l?.matchKey ?? l?.eventId ?? `${legs.length + n}`).join('+');
  return legsOnto(ticket, list, universe, { id: `${multi?.id}-add${mark}`, derived: true });
}

/**
 * Legs onto a ticket — the body `withLegs` and `buildTicket` share, because a
 * leg going onto a four-leg multi and a leg going onto nothing at all are the
 * same act with the same refusals.
 */
function legsOnto(ticket, list, universe, { id, derived }) {
  const multi = ticket?.multi;
  const legs = Array.isArray(ticket?.legs) ? ticket.legs : [];
  if (!multi || multi.status !== 'proposed') return null;
  if (!list.length || !list.every(priced) || !legs.every(priced)) return null;

  const max = maxLegsFor(multi);
  if (legs.length + list.length > max) throw new Error(legsFull(max));

  // Said in our own words before `combineLegs` says it in its own: the check is
  // the same one, and `assertIndependent` throws a sentence written for a
  // developer reading a stack trace. The added legs are checked against each
  // other too — two sides of one match is the mistake this stops, and the
  // picker is where both halves of it are within reach.
  const seen = new Set(legs.map((l, n) => fixtureOf(l, universe, n)));
  list.forEach((leg, n) => {
    const key = fixtureOf(leg, universe, `added-${n}`);
    if (seen.has(key)) throw new Error(SAME_MATCH);
    seen.add(key);
  });

  const at = (l) => Date.parse(l?.commenceTime ?? '') || Infinity;
  const next = [...legs, ...list].sort((a, b) => at(a) - at(b));

  try {
    return reprint(multi, next, id, universe, { derived });
  } catch {
    // The only thing `combineLegs` refuses is two legs on one match, and the
    // check above has already had its say. A crash out of a control the punter
    // tapped is never the right answer.
    throw new Error(SAME_MATCH);
  }
}

/** What a ticket the punter assembled themselves calls itself, for ever. */
export const BUILT_LABEL = 'Built by hand';

/**
 * AN EMPTY TICKET, for a multi somebody is building from the board rather than
 * reading off the pad. It is a proposal like any other — `status: 'proposed'`,
 * a stake, a book — with no legs on it yet and no strategy behind it.
 *
 * `strategyId: null` with a frozen `label` is the shape History and Insights
 * already hold for a bet the app did not print (`handTicket` in lib/tickets.js
 * is the other one), so the punter's own multis group under their own name
 * without a strategy document having to exist for them.
 */
export function newTicket({
  id, stake, bookmaker = null, bookmakerKey = null, forDate = null, oddsFetchedAt = null,
} = {}) {
  return {
    multi: {
      id,
      tier: null,
      forDate,
      status: 'proposed',
      strategyId: null,
      label: BUILT_LABEL,
      builtByHand: true,
      stake,
      bookmaker,
      bookmakerKey,
      oddsFetchedAt,
      combinedOdds: null,
      combinedProbability: null,
      potentialReturn: null,
    },
    legs: [],
  };
}

/**
 * The punter's own multi: `{ multi, legs }` from a handful of legs and nothing
 * else, or null where there is nothing to print.
 *
 * Every figure on it comes through the SAME `reprint` a dealt ticket's edit
 * does — the odds, the combined chance, the tier it prints in, the fair price
 * and the EV — so a ticket somebody built themselves is priced exactly as one
 * the engine printed, and the paper trail can score the two against each other.
 * Classic's rule applies to it, because it has no model: EV off the fair price.
 *
 * Throws `SAME_MATCH` on two sides of one match, like every other way legs go
 * onto a ticket. No leg cap — see `maxLegsFor`.
 */
export function buildTicket(legs, opts = {}, universe = null) {
  const list = (Array.isArray(legs) ? legs : [legs]).filter(Boolean);
  return legsOnto(newTicket(opts), list, universe, { id: opts.id, derived: false });
}

/**
 * What else the board could put on this ticket, best first:
 * `[{ matchKey, side, selection, match, sport, competition, kickoff, price,
 *    bookmakerKey, edge, books, thin }]`.
 *
 * BEST IS THE PRICE AGAINST THE OTHER BOOKS, and nothing else. `edge` is
 * `price × pFair − 1` where pFair is the market's fair probability with the
 * book being taken left OUT of the vote (`fairExcluding`) — the same excluded
 * form `outliers.js` flags a mispriced book on, and for the same reason: a
 * consensus that includes the book being measured is pulled toward it exactly
 * where it is most out of line. It is a statement about two prices, so it is
 * the same rule for every strategy, Classic included — no model is asked for
 * and none is implied.
 *
 * A side fewer than `MIN_BOOKS` books have priced is not ranked at all, it is
 * carried at the foot marked `thin`: an edge resting on one other book is a
 * disagreement, not a finding. `books` is the whole market's count, the book
 * being taken included, exactly as the detector counts it.
 *
 * Matches already on the ticket are gone, and so are matches too close to
 * kick-off to be placed — `allowStarted` moves that line exactly where the
 * timeline moves it, because a leg that cannot be rung through is not an option.
 *
 * MORE THAN ONE BOARD, because the app has more than one: the sports board and
 * the racing card are two universes and a punter building a multi does not
 * care which is which. Pass an array and they are read in order, ranked
 * together on the one test — a race's runners come through `selectionsOf` the
 * same way a fixture's two sides do, and a race already on the ticket is taken
 * out by its own `matchKey`, which is what stops two runners from one race.
 */
export function rankCandidates(ticket, universe, {
  book = null,
  allowStarted = false,
  now = Date.now(),
  sort = 'value',
} = {}) {
  const multi = ticket?.multi;
  const legs = Array.isArray(ticket?.legs) ? ticket.legs : [];
  const boards = (Array.isArray(universe) ? universe : [universe]).filter((u) => u?.matches?.values);
  if (!multi || !boards.length) return [];

  // Named off the FIRST board, which is the sports one: a Classic leg carries
  // no `matchKey` and has to be looked up, and it can only ever have come from
  // there. Every leg written since — the engine's, the picker's, a racing one —
  // carries its own key and never reaches the lookup.
  const on = new Set();
  legs.forEach((leg, n) => on.add(fixtureOf(leg, boards[0], n)));

  const rows = [];
  for (const board of boards) {
    for (const match of board.matches.values()) {
      if (!match?.matchKey || on.has(match.matchKey)) continue;
      const starts = Date.parse(match.kickoff ?? '');
      if (!Number.isFinite(starts)) continue;
      if (starts <= (allowStarted ? now : now + LIVE_BUFFER_MS)) continue;

      for (const side of selectionsOf(match)) {
        if (!(match.prices?.[side]?.length > 0)) continue;
        const built = legForTicket(multi, match, side, { bookmaker: book });
        if (!built) continue;
        const fair = fairExcluding(match, side, built.leg.bookmakerKey);
        // THE WHOLE MARKET'S COUNT, with the book being taken among it — the
        // same count `outliers.js` holds `MIN_BOOKS` against, so that the fair
        // price a row is measured on always rests on at least two other
        // opinions.
        const books = consensus(match, side)?.books ?? 0;
        rows.push({
          matchKey: match.matchKey,
          side,
          selection: built.leg.selection,
          match: built.leg.match,
          sport: match.sport ?? null,
          competition: match.competition ?? null,
          // What the chips and the eyebrow print: "AFL" for a fixture, "Horses"
          // for a race, where the competition is the meeting.
          sportTitle: match.sportTitle ?? match.competition ?? null,
          kickoff: match.kickoff ?? null,
          price: built.leg.price,
          bookmakerKey: built.leg.bookmakerKey,
          edge: fair ? built.leg.price * fair.pFair - 1 : null,
          books,
          thin: books < MIN_BOOKS,
        });
      }
    }
  }

  const soonest = (a, b) => Date.parse(a.kickoff) - Date.parse(b.kickoff);
  // An explicit tie-break every step of the way, so the same board always ranks
  // the same way — the rule the engine's own sorts hold.
  const stable = (a, b) => String(a.matchKey).localeCompare(String(b.matchKey))
    || String(a.side).localeCompare(String(b.side));
  return rows.sort((a, b) => {
    if (a.thin !== b.thin) return a.thin ? 1 : -1;
    if (sort === 'kickoff' || a.thin) return soonest(a, b) || (b.edge ?? -Infinity) - (a.edge ?? -Infinity) || stable(a, b);
    return (b.edge ?? -Infinity) - (a.edge ?? -Infinity) || soonest(a, b) || stable(a, b);
  });
}

/**
 * The same candidates as MATCH rows, which is how a betting screen reads:
 * `[{ matchKey, match, sport, competition, kickoff, sides, edge, thin }]`.
 *
 * A punter does not scan a list of selections, they scan a list of games and
 * then pick a side — so the fixture is the row and the sides are the controls
 * on it. A match ranks by its BETTER side: the reason it is worth a look is the
 * best thing on it, and burying a good price under the other team's bad one
 * would sort the list by nothing anybody cares about.
 */
export function matchRows(rows, { sort = 'value' } = {}) {
  const by = new Map();
  for (const row of rows ?? []) {
    const found = by.get(row.matchKey) ?? {
      matchKey: row.matchKey,
      match: row.match,
      sport: row.sport,
      competition: row.competition,
      sportTitle: row.sportTitle ?? row.competition ?? null,
      kickoff: row.kickoff,
      sides: [],
    };
    found.sides.push(row);
    by.set(row.matchKey, found);
  }

  const out = [...by.values()].map((m) => {
    const edges = m.sides.map((s) => s.edge).filter((e) => Number.isFinite(e));
    return { ...m, edge: edges.length ? Math.max(...edges) : null, thin: m.sides.every((s) => s.thin) };
  });

  const soonest = (a, b) => Date.parse(a.kickoff) - Date.parse(b.kickoff);
  const stable = (a, b) => String(a.matchKey).localeCompare(String(b.matchKey));
  return out.sort((a, b) => {
    if (a.thin !== b.thin) return a.thin ? 1 : -1;
    if (sort === 'kickoff' || a.thin) return soonest(a, b) || stable(a, b);
    return (b.edge ?? -Infinity) - (a.edge ?? -Infinity) || soonest(a, b) || stable(a, b);
  });
}

/**
 * The rows a search box and a sport chip leave behind. Pure, and it does not
 * re-sort: filtering is not a reason for the list to reorder itself under
 * somebody who is reading it.
 *
 * `query` is matched case-insensitively against the fixture line, the sport's
 * printed name and every side's own wording — so "coll", "collingwood v
 * carlton" and "afl" all find the same row, which is what a person typing
 * three letters expects. `sport` is the board's own key, as the chips carry it.
 *
 * A RACE ANSWERS TO THREE THINGS a person actually types: the venue and the
 * race number, which are both in the fixture line ("Randwick R5", so "randwick"
 * and "r5" both land), and the runner, which is in the side's own wording
 * ("Winx (4) to win"). Nothing here had to learn what a race is.
 */
export function filterCandidates(rows, { query = '', sport = null } = {}) {
  const want = String(query ?? '').trim().toLowerCase();
  return (rows ?? []).filter((row) => {
    if (sport && row.sport !== sport) return false;
    if (!want) return true;
    const hay = [
      row.match,
      row.competition,
      row.sportTitle,
      row.sport,
      ...(row.sides ?? []).map((s) => s.selection),
      row.selection,
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(want);
  });
}
